import { Injectable, ApplicationRef, inject } from '@angular/core';
import { SwUpdate, VersionReadyEvent } from '@angular/service-worker';
import { filter, first, interval, concat } from 'rxjs';

/**
 * Experience App Update Service
 * 
 * Strategy: Auto-reload immediately when new version detected
 * Rationale: Experience sessions are ephemeral, no risk of data loss
 * 
 * @since 2.0.0-beta.16
 */
@Injectable({ providedIn: 'root' })
export class ExperienceUpdateService {
  private swUpdate = inject(SwUpdate);
  private appRef = inject(ApplicationRef);

  constructor() {
    if (this.swUpdate.isEnabled) {
      this.initUpdateChecks();
      this.handleUpdates();
      this.handleUnrecoverableState();
    }
  }

  /**
   * Check for updates periodically
   */
  private initUpdateChecks(): void {
    // Check once app is stable
    const appIsStable$ = this.appRef.isStable.pipe(first(isStable => isStable === true));
    
    // Check every 5 minutes after app becomes stable
    const everySixHours$ = interval(5 * 60 * 1000);
    const everySixHoursOnceAppIsStable$ = concat(appIsStable$, everySixHours$);

    everySixHoursOnceAppIsStable$.subscribe(async () => {
      try {
        const updateFound = await this.swUpdate.checkForUpdate();
        console.log('[PWA] Update check:', updateFound ? 'Update available' : 'Already on latest version');
      } catch (err) {
        console.error('[PWA] Failed to check for updates:', err);
      }
    });

    // Also check when user returns to tab
    if (typeof document !== 'undefined') {
      document.addEventListener('visibilitychange', async () => {
        if (!document.hidden) {
          try {
            await this.swUpdate.checkForUpdate();
          } catch (err) {
            console.error('[PWA] Failed to check for updates on visibility change:', err);
          }
        }
      });
    }
  }

  /**
   * Handle version ready events - AUTO RELOAD for Experience
   */
  private handleUpdates(): void {
    this.swUpdate.versionUpdates
      .pipe(filter((evt): evt is VersionReadyEvent => evt.type === 'VERSION_READY'))
      .subscribe(evt => {
        console.log('[PWA] Update detected. Current version:', evt.currentVersion, 'New version:', evt.latestVersion);
        console.log('[PWA] Auto-reloading to activate new version...');
        this.reloadApp();
      });
  }

  /**
   * Handle unrecoverable state
   */
  private handleUnrecoverableState(): void {
    this.swUpdate.unrecoverable.subscribe(event => {
      console.error('[PWA] Unrecoverable state:', event.reason);
      console.log('[PWA] Reloading app to recover...');
      this.reloadApp();
    });
  }

  /**
   * Reload app immediately
   */
  private reloadApp(): void {
    console.log('[PWA] Reloading app to activate new version...');
    // Small delay to ensure logs are flushed
    setTimeout(() => {
      document.location.reload();
    }, 100);
  }

  /**
   * Manual check for updates (called from button/menu)
   */
  async checkForUpdate(): Promise<boolean> {
    try {
      const updateFound = await this.swUpdate.checkForUpdate();
      console.log('[PWA] Manual update check:', updateFound ? 'Update available' : 'Already on latest version');
      return updateFound;
    } catch (err) {
      console.error('[PWA] Failed to check for updates:', err);
      return false;
    }
  }
}
